/*
  File: cart-drawer-size-picker.js
  Version: 1.0.0
  Date: 2026-04-28
  Purpose: <cart-size-picker> Web Component — replaces the static size
  pill on each cart line with a button that opens an anchored popover
  listing the product's other sizes. Click a size → POST /cart/change.js
  via cart-state.changeLineVariantByKey → drawer section refreshes in place.

  The popover is portaled to document.body so it escapes the cart
  drawer's overflow clipping (cart body is overflow-y:auto +
  overflow-x:hidden; an absolutely-positioned popover inside would clip).
  Position is fixed, derived from the trigger's bounding rect on open;
  closes on Escape, outside-click, or any cart scroll.

  v1: size-only variant axis. Variant matrix derived from
  /products/<handle>.js on first open; cached on the element instance.
*/

(function () {
  'use strict';

  const PRODUCT_CACHE = Object.create(null);

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function fetchProduct(handle) {
    if (PRODUCT_CACHE[handle]) return Promise.resolve(PRODUCT_CACHE[handle]);
    return fetch('/products/' + encodeURIComponent(handle) + '.js', {
      headers: { Accept: 'application/json' },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => {
        if (p) PRODUCT_CACHE[handle] = p;
        return p;
      })
      .catch((e) => {
        console.error('[cart-size-picker] product fetch failed', handle, e);
        return null;
      });
  }

  /**
   * Find the variant matching the current variant on every option except
   * size, paired with each size value. Returns:
   *   [{ size, variant, available, inventoryQuantity, inventoryManaged, isCurrent }]
   *
   * variant is null when no SKU exists for that size combination — UI
   * treats null as out-of-stock.
   */
  function buildSizeMatrix(product, currentVariantId) {
    if (!product || !Array.isArray(product.variants) || !Array.isArray(product.options)) {
      return [];
    }
    // options is array of objects: { name, position, values }
    // (Or strings on older response shapes — defensively handle both.)
    const options = product.options.map((o) => (typeof o === 'string' ? { name: o } : o));
    const sizeIdx = options.findIndex((o) => String(o.name || '').toLowerCase() === 'size');
    if (sizeIdx === -1) return [];

    const currentVariant = product.variants.find((v) => String(v.id) === String(currentVariantId));
    if (!currentVariant) return [];

    // Collect unique size values, preserving the catalog's own ordering
    // (Shopify lets the merchant arrange variants; that order is the
    // intended display order — we don't sort numerically because not all
    // size domains are numeric).
    const sizesSeen = new Set();
    const sizes = [];
    product.variants.forEach((v) => {
      const sizeVal = v['option' + (sizeIdx + 1)];
      if (sizeVal && !sizesSeen.has(sizeVal)) {
        sizesSeen.add(sizeVal);
        sizes.push(sizeVal);
      }
    });

    return sizes.map((sizeVal) => {
      // Find the variant that matches the current on every non-size axis
      // and has this size. If none, this size is unavailable for the
      // current product config (rare but possible for products with
      // multiple option axes — we still flag it as OOS).
      const matchingVariant = product.variants.find((v) => {
        if (v['option' + (sizeIdx + 1)] !== sizeVal) return false;
        for (let i = 0; i < options.length; i++) {
          if (i === sizeIdx) continue;
          const key = 'option' + (i + 1);
          if (v[key] !== currentVariant[key]) return false;
        }
        return true;
      });
      const available = matchingVariant ? !!matchingVariant.available : false;
      const inventoryManaged = matchingVariant ? matchingVariant.inventory_management != null : false;
      const inventoryQuantity = matchingVariant && inventoryManaged
        ? Number(matchingVariant.inventory_quantity) || 0
        : null;
      return {
        size: sizeVal,
        variant: matchingVariant || null,
        available: available,
        inventoryQuantity: inventoryQuantity,
        inventoryManaged: inventoryManaged,
        isCurrent: matchingVariant ? String(matchingVariant.id) === String(currentVariantId) : false,
      };
    });
  }

  class CartSizePicker extends HTMLElement {
    connectedCallback() {
      this.__trigger = this.querySelector('[data-size-trigger]');
      this.__popover = null;
      this.__open = false;
      this.__loading = false;

      this.__onTriggerClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggle();
      };
      this.__onDocClick = (e) => {
        if (!this.__open) return;
        if (this.__popover && this.__popover.contains(e.target)) return;
        if (this.contains(e.target)) return;
        this.close();
      };
      this.__onKeyDown = (e) => {
        if (e.key === 'Escape' && this.__open) {
          this.close();
          if (this.__trigger) this.__trigger.focus();
        }
      };
      // Close on cart-body scroll only — NOT on any document-wide scroll.
      // The capture-phase document listener used previously fired for
      // unrelated scrolls (suggestion image loads, View Transitions,
      // anything in the page that bumped a scroll position) and the
      // popover would mysteriously vanish a few seconds after open.
      // Scope it to <cart-drawer-items>, the actual scroll container.
      this.__scrollHost = this.closest('cart-drawer-items');
      this.__onScroll = () => {
        if (this.__open) this.close();
      };
      this.__onResize = () => {
        if (this.__open) this.close();
      };

      if (this.__trigger) this.__trigger.addEventListener('click', this.__onTriggerClick);
    }

    disconnectedCallback() {
      this.close();
      if (this.__trigger) this.__trigger.removeEventListener('click', this.__onTriggerClick);
    }

    toggle() {
      if (this.__open) this.close();
      else this.open();
    }

    open() {
      if (this.__open || this.__loading) return;
      this.__loading = true;
      const handle = this.dataset.productHandle;
      const currentVariantId = this.dataset.currentVariantId;
      if (!handle || !currentVariantId) {
        this.__loading = false;
        return;
      }
      fetchProduct(handle).then((product) => {
        this.__loading = false;
        if (!product) return;
        const matrix = buildSizeMatrix(product, currentVariantId);
        if (!matrix.length) return;
        this.__matrix = matrix;
        this.__renderPopover(matrix);
      });
    }

    close() {
      if (!this.__open) return;
      this.__open = false;
      if (this.__trigger) this.__trigger.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', this.__onDocClick, true);
      document.removeEventListener('keydown', this.__onKeyDown);
      if (this.__scrollHost) this.__scrollHost.removeEventListener('scroll', this.__onScroll);
      window.removeEventListener('resize', this.__onResize);
      if (this.__popover && this.__popover.parentNode) {
        this.__popover.parentNode.removeChild(this.__popover);
      }
      this.__popover = null;
    }

    __renderPopover(matrix) {
      const threshold = Number(this.dataset.lowStockThreshold) || 3;
      const sizeGuideUrl = this.dataset.sizeGuideUrl || '';

      const pop = document.createElement('div');
      pop.className = 'cd-size-popover';
      pop.setAttribute('role', 'listbox');
      pop.setAttribute('aria-label', 'Select size');

      const buttonsHtml = matrix
        .map((entry) => {
          const stateAttrs = [];
          let cls = 'cd-size-popover__btn';
          if (entry.isCurrent) cls += ' is-current';
          if (!entry.available) cls += ' is-oos';
          const isLowStock =
            entry.available &&
            !entry.isCurrent &&
            entry.inventoryManaged &&
            entry.inventoryQuantity !== null &&
            entry.inventoryQuantity > 0 &&
            entry.inventoryQuantity <= threshold;
          if (isLowStock) cls += ' is-low-stock';

          const disabled = !entry.available ? ' disabled' : '';
          const ariaSelected = entry.isCurrent ? ' aria-selected="true"' : ' aria-selected="false"';
          const titleAttr = isLowStock
            ? ' title="Only ' + entry.inventoryQuantity + ' left"'
            : '';
          const variantId = entry.variant ? entry.variant.id : '';

          return (
            '<button type="button" role="option" class="' +
            cls +
            '"' +
            ariaSelected +
            disabled +
            titleAttr +
            ' data-size-option="' +
            escapeHtml(entry.size) +
            '"' +
            (variantId ? ' data-variant-id="' + escapeHtml(String(variantId)) + '"' : '') +
            '>' +
            '<span class="cd-size-popover__btn-label">' +
            escapeHtml(entry.size) +
            '</span>' +
            (isLowStock
              ? '<span class="cd-size-popover__lowstock-dot" aria-hidden="true"></span>'
              : '') +
            '</button>'
          );
        })
        .join('');

      const guideLink = sizeGuideUrl
        ? '<a class="cd-size-popover__guide" href="' +
          escapeHtml(sizeGuideUrl) +
          '" target="_blank" rel="noopener">Size guide</a>'
        : '<span></span>';

      pop.innerHTML =
        '<div class="cd-size-popover__head">Select size</div>' +
        '<div class="cd-size-popover__grid">' +
        buttonsHtml +
        '</div>' +
        '<div class="cd-size-popover__foot">' +
        '<span class="cd-size-popover__legend">' +
        '<span class="cd-size-popover__legend-dot" aria-hidden="true"></span>' +
        'Low stock' +
        '</span>' +
        guideLink +
        '</div>';

      pop.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-size-option]');
        if (!btn || btn.disabled) return;
        e.preventDefault();
        e.stopPropagation();
        const variantId = btn.getAttribute('data-variant-id');
        if (!variantId) {
          this.close();
          return;
        }
        if (btn.classList.contains('is-current')) {
          this.close();
          return;
        }
        this.__handleSizePick(variantId);
      });

      document.body.appendChild(pop);
      this.__popover = pop;
      this.__open = true;
      if (this.__trigger) this.__trigger.setAttribute('aria-expanded', 'true');

      this.__positionPopover();

      // Defer listener attach to next tick so the click that opened the
      // popover doesn't immediately bubble into the outside-click handler.
      Promise.resolve().then(() => {
        document.addEventListener('click', this.__onDocClick, true);
        document.addEventListener('keydown', this.__onKeyDown);
        if (this.__scrollHost) this.__scrollHost.addEventListener('scroll', this.__onScroll, { passive: true });
        window.addEventListener('resize', this.__onResize);
      });
    }

    __positionPopover() {
      if (!this.__popover || !this.__trigger) return;
      const rect = this.__trigger.getBoundingClientRect();
      const popRect = this.__popover.getBoundingClientRect();
      const margin = 6;
      let top = rect.bottom + margin;
      let left = rect.left;
      // Keep within viewport horizontally.
      const overflowRight = left + popRect.width - window.innerWidth + 8;
      if (overflowRight > 0) left = Math.max(8, left - overflowRight);
      // Flip above the trigger if there's no room below.
      if (top + popRect.height > window.innerHeight - 8) {
        const above = rect.top - popRect.height - margin;
        if (above >= 8) top = above;
      }
      this.__popover.style.top = top + 'px';
      this.__popover.style.left = left + 'px';
    }

    __handleSizePick(newVariantId) {
      const lineKey = this.dataset.lineKey;
      if (!lineKey || !window.cartState || typeof window.cartState.changeLineVariantByKey !== 'function') {
        this.close();
        return;
      }

      // Optimistic UI: flip the trigger pill text + host data immediately
      // so the customer sees the size change land before the server
      // round-trip (remove-then-add can take 500–1500ms). The drawer
      // section refresh on success will replace this DOM wholesale with
      // server-rendered markup — our optimistic edits are throwaway. On
      // failure we revert from the snapshot below so the customer's view
      // matches the actual server cart.
      const newEntry = (this.__matrix || []).find(
        (e) => e.variant && String(e.variant.id) === String(newVariantId)
      );
      const triggerTextEl = this.querySelector('.cd-size-picker__trigger-text');
      const snapshot = {
        text: triggerTextEl ? triggerTextEl.textContent : '',
        currentSize: this.dataset.currentSize,
        currentVariantId: this.dataset.currentVariantId,
        ariaLabel: this.__trigger ? this.__trigger.getAttribute('aria-label') : '',
      };
      if (newEntry) {
        if (triggerTextEl) triggerTextEl.textContent = 'Size ' + newEntry.size;
        this.dataset.currentSize = newEntry.size;
        this.dataset.currentVariantId = String(newVariantId);
        if (this.__trigger) {
          this.__trigger.setAttribute(
            'aria-label',
            'Change size, currently ' + newEntry.size
          );
        }
      }

      // Close the popover first — the drawer section refresh that follows
      // will replace the trigger's host element (this), and we want our
      // portaled popover gone before that swap.
      this.close();

      window.cartState.changeLineVariantByKey(lineKey, newVariantId).then((result) => {
        if (result && result.ok) {
          // Notify any listener (e.g. row-glow effect) that the line changed.
          // Drawer section refresh replaces this element's HTML, so no
          // further DOM work needed here.
          this.dispatchEvent(
            new CustomEvent('cart-size:changed', {
              bubbles: true,
              detail: { lineKey: lineKey, variantId: newVariantId },
            })
          );
        } else {
          // Server rejected (e.g. new variant just sold out). Revert the
          // optimistic edits so the trigger reflects the actual cart.
          if (triggerTextEl && triggerTextEl.parentNode) {
            triggerTextEl.textContent = snapshot.text;
          }
          if (snapshot.currentSize !== undefined) this.dataset.currentSize = snapshot.currentSize;
          if (snapshot.currentVariantId !== undefined)
            this.dataset.currentVariantId = snapshot.currentVariantId;
          if (this.__trigger && snapshot.ariaLabel) {
            this.__trigger.setAttribute('aria-label', snapshot.ariaLabel);
          }
        }
      });
    }
  }

  if (!customElements.get('cart-size-picker')) {
    customElements.define('cart-size-picker', CartSizePicker);
  }
})();
